import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  ArrowRight,
  Briefcase,
  Building2,
  CheckCircle,
  GraduationCap,
  Handshake,
  Users,
} from 'lucide-react';

interface InternshipFormData {
  name: string;
  email: string;
  phone: string;
  college: string;
  engineeringBranch: string;
  yearOfStudy: string;
  preferredArea: string;
  duration: string;
  message: string;
}

interface PartnershipFormData {
  name: string;
  organization: string;
  email: string;
  phone: string;
  city: string;
  experience: string;
  message: string;
}

const inputClassName =
  'w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100';

const InternshipPartnershipsPage: React.FC = () => {
  const [submittedForm, setSubmittedForm] = useState<string | null>(null);
  const internshipForm = useForm<InternshipFormData>();
  const franchiseForm = useForm<PartnershipFormData>();
  const agentForm = useForm<PartnershipFormData>();

  const submitByEmail = (
    formName: string,
    subject: string,
    data: Record<string, string>,
    reset: () => void,
  ) => {
    const body = Object.entries(data)
      .map(([label, value]) => `${label}: ${value || 'Not provided'}`)
      .join('\n');
    window.location.href = `mailto:infonammasewa@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSubmittedForm(formName);
    reset();
  };

  const renderError = (message?: string) =>
    message ? <p className="mt-1 text-sm text-red-600">{message}</p> : null;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <section className="bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-900 px-4 py-20 text-white">
        <div className="mx-auto max-w-6xl text-center">
          <p className="mb-4 font-semibold uppercase tracking-[0.2em] text-cyan-300">
            SEWA opportunities
          </p>
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-6xl">
            Engineering Internships &amp; Partnerships
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-blue-100 sm:text-xl">
            Build practical engineering experience, connect students with industry,
            and grow education access with SEWA.
          </p>
          <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
            <a
              href="#internship-application"
              className="inline-flex items-center justify-center rounded-lg bg-cyan-400 px-6 py-3 font-bold text-slate-950 transition hover:bg-cyan-300"
            >
              Apply for an internship <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <a
              href="#partnerships"
              className="inline-flex items-center justify-center rounded-lg border border-white/40 px-6 py-3 font-bold text-white transition hover:bg-white/10"
            >
              Become a partner
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-6 px-4 py-16 md:grid-cols-3">
        {[
          {
            icon: <Briefcase className="h-8 w-8" />,
            title: 'Industry-ready experience',
            text: 'Work on practical projects and develop communication, teamwork, and professional skills.',
          },
          {
            icon: <GraduationCap className="h-8 w-8" />,
            title: 'Engineering first',
            text: 'Opportunities are designed for students from CSE, IT, ECE, EEE, Mechanical, Civil, and related branches.',
          },
          {
            icon: <Users className="h-8 w-8" />,
            title: 'Mentorship and guidance',
            text: 'Get structured support from mentors while exploring your preferred engineering career path.',
          },
        ].map((benefit) => (
          <div key={benefit.title} className="rounded-2xl bg-white p-7 shadow-sm ring-1 ring-slate-200">
            <div className="mb-4 text-blue-700">{benefit.icon}</div>
            <h2 className="text-xl font-bold">{benefit.title}</h2>
            <p className="mt-3 leading-7 text-slate-600">{benefit.text}</p>
          </div>
        ))}
      </section>

      <section id="internship-application" className="bg-white px-4 py-16">
        <div className="mx-auto max-w-5xl">
          <div className="mb-10">
            <p className="font-semibold uppercase tracking-wider text-blue-700">Student application</p>
            <h2 className="mt-2 text-3xl font-extrabold sm:text-4xl">Apply for an engineering internship</h2>
            <p className="mt-3 text-slate-600">
              Tell us about your interests and we will contact you about suitable internship opportunities.
            </p>
          </div>
          {submittedForm === 'internship' && (
            <div className="mb-6 flex items-center gap-3 rounded-lg bg-green-50 p-4 text-green-800">
              <CheckCircle className="h-5 w-5" /> Your application is ready to be sent. We will contact you soon.
            </div>
          )}
          <form
            onSubmit={internshipForm.handleSubmit((data) =>
              submitByEmail(
                'internship',
                'Engineering Internship Application',
                {
                  'Full Name': data.name,
                  Email: data.email,
                  Phone: data.phone,
                  College: data.college,
                  'Engineering Branch': data.engineeringBranch,
                  'Year of Study': data.yearOfStudy,
                  'Preferred Area': data.preferredArea,
                  'Preferred Duration': data.duration,
                  Message: data.message,
                },
                internshipForm.reset,
              ),
            )}
            className="grid gap-5 md:grid-cols-2"
          >
            <label>Full name *
              <input {...internshipForm.register('name', { required: 'Please enter your name' })} className={inputClassName} placeholder="Your full name" />
              {renderError(internshipForm.formState.errors.name?.message)}
            </label>
            <label>Email address *
              <input type="email" {...internshipForm.register('email', { required: 'Please enter your email' })} className={inputClassName} placeholder="you@example.com" />
              {renderError(internshipForm.formState.errors.email?.message)}
            </label>
            <label>Phone number *
              <input type="tel" {...internshipForm.register('phone', { required: 'Please enter your phone number' })} className={inputClassName} placeholder="+91 98765 43210" />
              {renderError(internshipForm.formState.errors.phone?.message)}
            </label>
            <label>College / institution *
              <input {...internshipForm.register('college', { required: 'Please enter your college' })} className={inputClassName} placeholder="College name" />
              {renderError(internshipForm.formState.errors.college?.message)}
            </label>
            <label>Engineering branch *
              <select {...internshipForm.register('engineeringBranch', { required: 'Please select your branch' })} className={inputClassName} defaultValue="">
                <option value="" disabled>Select your branch</option>
                <option>CSE / IT / AI &amp; Data Science</option>
                <option>ECE / EEE / EIE</option>
                <option>Mechanical / Mechatronics / Automobile</option>
                <option>Civil / Environmental</option>
                <option>Other engineering branch</option>
              </select>
              {renderError(internshipForm.formState.errors.engineeringBranch?.message)}
            </label>
            <label>Year of study *
              <select {...internshipForm.register('yearOfStudy', { required: 'Please select your year' })} className={inputClassName} defaultValue="">
                <option value="" disabled>Select year</option>
                <option>1st year</option><option>2nd year</option><option>3rd year</option><option>Final year</option><option>Recent graduate</option>
              </select>
              {renderError(internshipForm.formState.errors.yearOfStudy?.message)}
            </label>
            <label>Preferred internship area *
              <input {...internshipForm.register('preferredArea', { required: 'Please enter an area' })} className={inputClassName} placeholder="Software, core engineering, design..." />
              {renderError(internshipForm.formState.errors.preferredArea?.message)}
            </label>
            <label>Preferred duration *
              <select {...internshipForm.register('duration', { required: 'Please select a duration' })} className={inputClassName} defaultValue="">
                <option value="" disabled>Select duration</option><option>1 month</option><option>2-3 months</option><option>4-6 months</option>
              </select>
              {renderError(internshipForm.formState.errors.duration?.message)}
            </label>
            <label className="md:col-span-2">Additional information
              <textarea {...internshipForm.register('message')} className={inputClassName} rows={4} placeholder="Projects, skills, portfolio link, or questions" />
            </label>
            <button type="submit" className="md:col-span-2 rounded-lg bg-blue-700 px-6 py-3 font-bold text-white transition hover:bg-blue-800">
              Submit internship application
            </button>
          </form>
        </div>
      </section>

      <section id="partnerships" className="bg-slate-100 px-4 py-16">
        <div className="mx-auto max-w-6xl">
          <div className="mb-10 text-center">
            <p className="font-semibold uppercase tracking-wider text-blue-700">Partner with SEWA</p>
            <h2 className="mt-2 text-3xl font-extrabold sm:text-4xl">Franchise and admission agent opportunities</h2>
            <p className="mx-auto mt-3 max-w-2xl text-slate-600">
              Join our network to help students discover trusted education pathways and expand access in your region.
            </p>
          </div>
          <div className="grid gap-8 lg:grid-cols-2">
            {[
              {
                id: 'franchise',
                title: 'Franchise enquiry',
                icon: <Building2 className="h-7 w-7" />,
                description: 'For education entrepreneurs interested in operating a SEWA centre.',
                form: franchiseForm,
                subject: 'SEWA Franchise Enquiry',
              },
              {
                id: 'agent',
                title: 'Admission agent registration',
                icon: <Handshake className="h-7 w-7" />,
                description: 'For counsellors and agents who want to support student admissions with SEWA.',
                form: agentForm,
                subject: 'SEWA Admission Agent Registration',
              },
            ].map((partnership) => (
              <div key={partnership.id} className="rounded-2xl bg-white p-7 shadow-sm ring-1 ring-slate-200">
                <div className="mb-5 flex items-center gap-3 text-blue-700">
                  {partnership.icon}
                  <h3 className="text-2xl font-bold text-slate-900">{partnership.title}</h3>
                </div>
                <p className="mb-6 text-slate-600">{partnership.description}</p>
                {submittedForm === partnership.id && (
                  <div className="mb-4 rounded-lg bg-green-50 p-3 text-sm text-green-800">Thank you. We will review your enquiry and contact you.</div>
                )}
                <form
                  onSubmit={partnership.form.handleSubmit((data) =>
                    submitByEmail(
                      partnership.id,
                      partnership.subject,
                      {
                        'Full Name': data.name,
                        Organization: data.organization,
                        Email: data.email,
                        Phone: data.phone,
                        City: data.city,
                        Experience: data.experience,
                        Message: data.message,
                      },
                      partnership.form.reset,
                    ),
                  )}
                  className="space-y-4"
                >
                  <input {...partnership.form.register('name', { required: 'Please enter your name' })} className={inputClassName} placeholder="Full name *" />
                  <input {...partnership.form.register('organization', { required: 'Please enter your organization' })} className={inputClassName} placeholder="Organization / business name *" />
                  <input type="email" {...partnership.form.register('email', { required: 'Please enter your email' })} className={inputClassName} placeholder="Email address *" />
                  <input type="tel" {...partnership.form.register('phone', { required: 'Please enter your phone number' })} className={inputClassName} placeholder="Phone number *" />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input {...partnership.form.register('city', { required: 'Please enter your city' })} className={inputClassName} placeholder="City / district *" />
                    <input {...partnership.form.register('experience')} className={inputClassName} placeholder="Years of experience" />
                  </div>
                  <textarea {...partnership.form.register('message')} className={inputClassName} rows={3} placeholder="Tell us about your interest" />
                  <button type="submit" className="w-full rounded-lg bg-slate-900 px-6 py-3 font-bold text-white transition hover:bg-blue-800">
                    Send enquiry
                  </button>
                </form>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default InternshipPartnershipsPage;
